import React, { useCallback, useState } from 'react';
import {
  Button,
  Text,
  Badge,
  SearchBox,
  Spinner,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  ArrowLeft20Regular,
  ArrowDownload20Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
  Dismiss20Regular,
  MoreVertical20Regular,
  Search20Regular,
  Edit24Regular,
  Delete24Regular,
} from '@fluentui/react-icons';
import { Box, useFluentTokens } from '../../../../theme/fluent';
import { useChatHistory } from './useChatHistory';
import { formatHistoryDate } from './chatHistory.utils';
import { useChatContext } from '../../providers/ChatProvider';
import { SessionEditDialog } from './SessionEditDialog';
import { SessionDeleteDialog } from './SessionDeleteDialog';
import type { ChatSession } from '../../types/domain';
import type { ChatHistoryViewProps, HistoryItem } from './chatHistory.types';

interface SectionHeaderProps {
  label: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ label, count, expanded, onToggle }) => {
  const { tokens, modeTokens } = useFluentTokens();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      aria-expanded={expanded}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        cursor: 'pointer',
        borderBottom: `1px solid ${modeTokens.colorNeutralStroke2}`,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = modeTokens.colorNeutralBackground1Hover; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <Box style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
        <Text weight="bold" size={300}>{label}</Text>
        <Badge size="small" appearance="outline">{count}</Badge>
      </Box>
      {expanded ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
    </Box>
  );
};

const HighlightedText: React.FC<{ text: string; query: string; highlightColor: string }> = ({
  text,
  query,
  highlightColor,
}) => {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ backgroundColor: highlightColor, borderRadius: '2px' }}>
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  );
};

interface HistoryListItemProps {
  item: HistoryItem;
  onSelect: (item: HistoryItem) => void;
  onMenuClick: (e: React.MouseEvent<HTMLButtonElement>, item: HistoryItem) => void;
  searchQuery?: string;
}

const HistoryListItem: React.FC<HistoryListItemProps> = React.memo(
  ({ item, onSelect, onMenuClick, searchQuery = '' }) => {
    const { tokens, modeTokens } = useFluentTokens();

    const handleSelect = useCallback(() => onSelect(item), [item, onSelect]);
    const handleMenuClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onMenuClick(e, item);
      },
      [item, onMenuClick],
    );

    return (
      <Box style={{ borderBottom: `1px solid ${modeTokens.colorNeutralStroke2}` }}>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
            gap: tokens.spacingHorizontalS,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = modeTokens.colorNeutralBackground1Hover; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <Box style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={handleSelect}>
            <Text
              weight="semibold"
              size={300}
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: modeTokens.colorNeutralForeground1,
                marginBottom: tokens.spacingVerticalXXS,
              }}
            >
              <HighlightedText
                text={item.title}
                query={searchQuery}
                highlightColor={modeTokens.colorBrandBackground2}
              />
            </Text>
            <Text size={200} style={{ color: modeTokens.colorNeutralForeground2, display: 'block' }}>
              {formatHistoryDate(item.date)}
            </Text>
          </Box>
          <Button
            appearance="subtle"
            icon={<MoreVertical20Regular />}
            size="small"
            onClick={handleMenuClick}
            aria-label="More options"
            style={{ flexShrink: 0 }}
          />
        </Box>
      </Box>
    );
  },
);
HistoryListItem.displayName = 'HistoryListItem';

interface SectionProps {
  label: string;
  totalCount: number;
  items: HistoryItem[];
  expanded: boolean;
  onToggle: () => void;
  onSelect: (item: HistoryItem) => void;
  onMenuClick: (e: React.MouseEvent<HTMLButtonElement>, item: HistoryItem) => void;
}

const Section: React.FC<SectionProps> = ({ label, totalCount, items, expanded, onToggle, onSelect, onMenuClick }) => {
  if (totalCount === 0) return null;
  return (
    <>
      <SectionHeader label={label} count={totalCount} expanded={expanded} onToggle={onToggle} />
      {expanded && items.map((item) => (
        <HistoryListItem key={item.id} item={item} onSelect={onSelect} onMenuClick={onMenuClick} />
      ))}
    </>
  );
};

export const ChatHistoryView: React.FC<ChatHistoryViewProps> = ({ onBack, onSelectSession }) => {
  const { tokens, modeTokens } = useFluentTokens();
  const { sessions, deleteSession, updateSessionTitle, isChatActionDisabled } = useChatContext();
  const {
    searchMode,
    searchQuery,
    expandedToday,
    expandedYesterday,
    expandedOlder,
    visibleOlderCount,
    isLoadingMore,
    hasMoreOlder,
    today,
    yesterday,
    older,
    filteredItems,
    setSearchQuery,
    setExpandedToday,
    setExpandedYesterday,
    setExpandedOlder,
    handleSearchToggle,
    handleSelect,
    handleLoadMore,
  } = useChatHistory(onSelectSession);

  // Context menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [activeItem, setActiveItem] = useState<HistoryItem | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const activeSession: ChatSession | null = activeItem
    ? (sessions.find((s) => s.id === activeItem.id) ?? null)
    : null;

  const handleMenuClick = useCallback((e: React.MouseEvent<HTMLButtonElement>, item: HistoryItem) => {
    setActiveItem(item);
    setMenuAnchorEl(e.currentTarget);
    setMenuOpen(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchorEl(null);
  }, []);

  const handleOpenEdit = useCallback(() => {
    if (activeItem) {
      setEditTitle(activeItem.title);
      setEditDialogOpen(true);
    }
    handleMenuClose();
  }, [activeItem, handleMenuClose]);

  const handleOpenDelete = useCallback(() => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleEditSave = useCallback(async () => {
    if (activeItem && editTitle.trim()) {
      setEditDialogOpen(false);
      await updateSessionTitle(activeItem.id, editTitle.trim());
      setActiveItem(null);
    }
  }, [activeItem, editTitle, updateSessionTitle]);

  const handleDeleteConfirm = useCallback(async () => {
    if (activeItem) {
      setDeleteDialogOpen(false);
      await deleteSession(activeItem.id);
      setActiveItem(null);
    }
  }, [activeItem, deleteSession]);

  const renderItem = useCallback((item: HistoryItem, query = '') => (
    <HistoryListItem key={item.id} item={item} onSelect={handleSelect} onMenuClick={handleMenuClick} searchQuery={query} />
  ), [handleSelect, handleMenuClick]);

  const groupedSections = (
    <>
      <Section
        label="Today"
        totalCount={today.length}
        items={today}
        expanded={expandedToday}
        onToggle={() => setExpandedToday((v) => !v)}
        onSelect={handleSelect}
        onMenuClick={handleMenuClick}
      />
      <Section
        label="Yesterday"
        totalCount={yesterday.length}
        items={yesterday}
        expanded={expandedYesterday}
        onToggle={() => setExpandedYesterday((v) => !v)}
        onSelect={handleSelect}
        onMenuClick={handleMenuClick}
      />
      <Section
        label="Older"
        totalCount={older.length}
        items={older.slice(0, visibleOlderCount)}
        expanded={expandedOlder}
        onToggle={() => setExpandedOlder((v) => !v)}
        onSelect={handleSelect}
        onMenuClick={handleMenuClick}
      />
      {(hasMoreOlder || isLoadingMore) && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
            borderTop: `1px solid ${modeTokens.colorNeutralStroke2}`,
          }}
        >
          {isLoadingMore ? (
            <Spinner
              size="tiny"
              label="Loading..."
              labelPosition="after"
              style={{ color: modeTokens.colorBrandForeground2 }}
            />
          ) : (
            <Button
              appearance="subtle"
              icon={<ArrowDownload20Regular />}
              onClick={handleLoadMore}
              style={{ color: modeTokens.colorBrandForeground2 }}
            >
              Load 10 More
            </Button>
          )}
        </Box>
      )}
    </>
  );

  const renderSearchBody = () => {
    if (searchQuery.trim() === '') return groupedSections;
    if (filteredItems.length === 0) {
      return (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalL}`,
            textAlign: 'center',
            gap: tokens.spacingVerticalS,
            height: '100%',
          }}
        >
          <Text weight="semibold" size={400} style={{ color: modeTokens.colorNeutralForeground1 }}>
            No Results Found
          </Text>
          <Text size={300} style={{ color: modeTokens.colorNeutralForeground2 }}>
            Search is limited to chats from the last 30 days.
          </Text>
        </Box>
      );
    }
    return (
      <>
        {filteredItems.map((item) => renderItem(item, searchQuery))}
      </>
    );
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: modeTokens.colorNeutralBackground1,
      }}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
          borderBottom: `1px solid ${modeTokens.colorNeutralStroke2}`,
          flexShrink: 0,
          minHeight: '48px',
        }}
      >
        <Button appearance="subtle" icon={<ArrowLeft20Regular />} size="small" onClick={onBack} aria-label="Go back" />
        <Text weight="semibold" size={400} style={{ color: modeTokens.colorBrandForeground2, flex: 1, textAlign: 'center' }}>
          Chat History
        </Text>
        <Button
          appearance="subtle"
          icon={<Search20Regular />}
          size="small"
          onClick={handleSearchToggle}
          aria-label={searchMode ? 'Close search' : 'Search history'}
          style={{ color: searchMode ? modeTokens.colorBrandForeground2 : undefined }}
        />
      </Box>

      {searchMode && (
        <Box
          style={{
            padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
            borderBottom: `1px solid ${modeTokens.colorNeutralStroke2}`,
            flexShrink: 0,
          }}
        >
          <SearchBox
            autoFocus
            placeholder="Search history..."
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value)}
            contentAfter={
              searchQuery ? (
                <Button
                  appearance="transparent"
                  icon={<Dismiss20Regular />}
                  size="small"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                />
              ) : undefined
            }
            style={{ display: 'flex', width: '100%', maxWidth: 'none' }}
          />
        </Box>
      )}

      <Box style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {searchMode ? renderSearchBody() : groupedSections}
      </Box>

      {/* Context menu for history items */}
      <Menu open={menuOpen} onOpenChange={(_e, data) => !data.open && handleMenuClose()} positioning={{ target: menuAnchorEl }}>
        <MenuTrigger disableButtonEnhancement>
          <span style={{ display: 'none' }} />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              icon={<Edit24Regular />}
              disabled={isChatActionDisabled}
              onClick={handleOpenEdit}
            >
              Rename
            </MenuItem>
            <MenuItem
              icon={<Delete24Regular style={{ color: modeTokens.colorDangerForeGround1 }} />}
              disabled={isChatActionDisabled}
              onClick={handleOpenDelete}
              style={{ color: modeTokens.colorDangerForeGround1 }}
            >
              Delete
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      {/* Edit dialog */}
      <SessionEditDialog
        open={editDialogOpen}
        editTitle={editTitle}
        onTitleChange={setEditTitle}
        onSave={handleEditSave}
        onCancel={() => { setEditDialogOpen(false); setActiveItem(null); }}
      />

      {/* Delete confirmation dialog */}
      <SessionDeleteDialog
        open={deleteDialogOpen}
        session={activeSession}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteDialogOpen(false); setActiveItem(null); }}
      />
    </Box>
  );
};
